/**
 * Centralized section slug constants.
 *
 * Single source of truth for the 50 section slugs used across client and
 * server code. When renaming or relocating a section, update the slug value
 * here and TypeScript will surface every consumer that needs updating.
 *
 * Section slugs are organized by their parent category for navigation.
 * Note: `password-log-*` slugs are legacy-prefix slugs that live under
 * the renamed `online-presence` category. They are NOT renamed; the prefix
 * is retained for stability.
 *
 * Related: shared/constants/categories.ts (Phase 1) for category slugs.
 * Field keys (Phase 3) queued for follow-up session.
 *
 * Established session 39 as constants migration Phase 2 after Phase 1
 * proved the pattern's value (commits b260b60 + e2a3883 + e69c5ea + the
 * pre-push slug check dfd2c60 caught a missed migration on Day 1).
 */

export const SECTION_SLUGS = {
  // About You
  ABOUT_YOU: 'about-you',

  // Protect Your Access (s111 Build 6)
  PROTECT_YOUR_ACCESS: 'protect-your-access',

  // My Wishes & Legacy
  INSTRUCTIONS_FOR_EXECUTORS_EXECUTOR_INFORMATION:  'instructions-for-executors-executor-information',
  INSTRUCTIONS_FOR_EXECUTORS_WHERE_ARE_THEY:        'instructions-for-executors-where-are-they',
  INSTRUCTIONS_FOR_EXECUTORS_LETTERS:               'instructions-for-executors-letters',
  INSTRUCTIONS_FOR_EXECUTORS_EXECUTOR_INSTRUCTIONS: 'instructions-for-executors-executor-instructions',

  // Custom priority items (associated with retired Do This First category)
  CUSTOM_PRIORITY_ITEMS: 'custom-priority-items',

  // Legal
  LEGAL_YOUR_EXECUTOR:        'legal-your-executor',
  LEGAL_IMPORTANT_DOCUMENTS:  'legal-important-documents',
  LEGAL_POWERS_OF_ATTORNEY:   'legal-powers-of-attorney',
  LEGAL_PROFESSIONAL_CONTACTS:'legal-professional-contacts',
  LEGAL_BUSINESS_DOCUMENTS:   'legal-business-documents',
  LEGAL_BUSINESS_CONTACTS:    'legal-business-contacts',

  // Financial and Insurance
  FINANCIAL_BANK_ACCOUNTS:                    'financial-bank-accounts',
  FINANCIAL_INVESTMENTS_RRSP:                 'financial-investments-rrsp',
  FINANCIAL_INCOME_PENSIONS:                  'financial-income-pensions',
  FINANCIAL_DEBTS_LIABILITIES:                'financial-debts-liabilities',
  FINANCIAL_TAXES:                            'financial-taxes',
  FINANCIAL_BUSINESS_SELF_EMPLOYMENT:         'financial-business-self-employment',
  FINANCIAL_AND_INSURANCE_PRE_AUTHORIZED_DEBITS: 'financial-and-insurance-pre-authorized-debits',
  FINANCIAL_AND_INSURANCE_INSURANCE_POLICIES: 'financial-and-insurance-insurance-policies',
  FINANCIAL_AND_INSURANCE_HOUSING:            'financial-and-insurance-housing',

  // Assets
  ASSETS_VEHICLE_LIST: 'assets-vehicle-list',
  ASSETS_PROPERTIES:   'assets-properties',
  ASSETS_VALUABLES:    'assets-valuables',
  // s232 (chapter two PR C, ruling 4): the Safes screen's rows, typed as located_items. In HIDDEN_SECTIONS — the
  // interview is its surface; no classic page draws it and it takes no part in progress.
  ASSETS_SAFES_LOCKED_PLACES: 'assets-safes-locked-places',

  // Personal Information
  PERSONAL_INFORMATION_GOVERNMENT_IDS:      'personal-information-government-ids',
  PERSONAL_INFORMATION_HEALTH_MEDICAL:      'personal-information-health-medical',
  INSTRUCTIONS_FOR_EXECUTORS_FUNERAL_WISHES: 'instructions-for-executors-funeral-wishes',
  PERSONAL_INFORMATION_HEALTH_INSURANCE:    'personal-information-health-insurance',
  PERSONAL_INFORMATION_EDUCATION:           'personal-information-education',
  PERSONAL_INFORMATION_CAREER:              'personal-information-career',
  PERSONAL_INFORMATION_OTHER_CREDENTIALS:   'personal-information-other-credentials',

  // Children and Dependents
  CHILDREN_DEPENDENTS_OVERVIEW:          'children-dependents-overview',
  CHILDREN_DEPENDENTS_HEALTH:            'children-dependents-health',
  CHILDREN_DEPENDENTS_DAILY_CARE:        'children-dependents-daily-care',
  CHILDREN_DEPENDENTS_EDUCATION:         'children-dependents-education',
  CHILDREN_DEPENDENTS_GUARDIANSHIP:      'children-dependents-guardianship',
  CHILDREN_DEPENDENTS_OTHER:             'children-dependents-other',
  CHILDREN_DEPENDENTS_VALUES_WISHES:     'children-dependents-values-wishes',
  CHILDREN_DEPENDENTS_FINANCIAL_SUPPORT: 'children-dependents-financial-support',
  CHILDREN_DEPENDENTS_GOV_IDS:           'children-dependents-gov-ids',

  // Family, Friends and Pets
  FAMILY_FRIENDS_AND_PETS_IMPORTANT_CONTACTS: 'family-friends-and-pets-important-contacts',
  FAMILY_PETS:                                'family-pets',

  // Online Presence (legacy password-log-* prefix retained)
  PASSWORD_LOG_DEVICES_LOCAL_ACCESS:            'password-log-devices-local-access',
  PASSWORD_LOG_PRIMARY_EMAIL_IDENTITY_ACCOUNTS: 'password-log-primary-email-identity-accounts',
  PASSWORD_LOG_GOVERNMENT_TAX_US_CANADA:        'password-log-government-tax-us-canada',
  PASSWORD_LOG_GOVERNMENT_TAX:                  'password-log-government-tax',
  PASSWORD_LOG_ONLINE_ACCOUNTS_COMMUNITIES:     'password-log-online-accounts-communities',
  PASSWORD_LOG_SHOPPING_LOYALTY_PROGRAMS:       'password-log-shopping-loyalty-programs',
  PASSWORD_LOG_DIGITAL_FILES_CLOUD_STORAGE:     'password-log-digital-files-cloud-storage',
  PASSWORD_LOG_SOCIAL_MEDIA_MESSAGING:          'password-log-social-media-messaging',
} as const;

export type SectionSlug = typeof SECTION_SLUGS[keyof typeof SECTION_SLUGS];
